package com.appcreator.blueprint.firebase.firestore

import com.appcreator.blueprint.core.DataShape
import com.appcreator.blueprint.core.DataSpec
import com.appcreator.blueprint.core.LoaderSpec
import com.appcreator.blueprint.core.findParameters
import com.appcreator.blueprint.dataspecs.ListDataSpec
import com.appcreator.blueprint.dataspecs.ValueDataSpec
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable


@Serializable
@SerialName("FirebaseFirestoreLoaderSpec")
data class FirebaseFirestoreLoaderSpec(
    override val id: String,
    override val parameterSettings: Map<String, LoaderSpec.ParameterSetting>?,
    val dataPath: String?,
    val queryScope: QueryScope?,
    val limit: Int?,
    val orderBy: List<Order>?,
    val where: List<Where>?,
    val dataValues: List<DataSpec>?,
    val loadStrategy: LoadStrategy?,
): LoaderSpec {

    companion object {
        const val documentId = "documentId"
        const val documentPath = "documentPath"
        const val orderBy = "orderBy"
    }
    val documentFetch: Boolean
        get() {
            val parts = dataPath?.split("/")?: emptyList()
            return parts.size % 2 == 0
        }

    override val dataShape: DataShape
        get() {
            val parts = dataPath?.split("/")?: emptyList()
            val dataValues = (dataValues?: emptyList())
                .plus(ValueDataSpec(documentId, ValueDataSpec.Type.String, null, true))
                .plus(ValueDataSpec(documentPath, ValueDataSpec.Type.String, null, true))
            return if (parts.size % 2 == 0) {
                DataShape(dataValues)
            } else {
                DataShape(listOf(ListDataSpec(parts.lastOrNull()?: "result", null, dataValues)))
            }
        }
    override val supportsPaging: Boolean get() = !documentFetch

    override val parameters: Set<String>
        get() {
            val pathParams = (dataPath?.findParameters()?: emptyList()) +
                    (where?.map {
                        when(it) {
                            is Where.Group -> TODO()
                            is Where.In -> TODO()
                            is Where.Value -> it.value.findParameters()
                        }
                    }?.flatten()?: emptyList()) +
                    (orderBy?.filter { it.direction == OrderBy.Parameter }
                        ?.map { "${it.field}-${Companion.orderBy}" } ?: emptyList())

            return pathParams.toSet()
        }

    enum class QueryScope {
        Collection,
        CollectionGroup
    }

    @Serializable
    data class Order(
        val field: String,
        val direction: OrderBy
    )

    enum class OrderBy {
        Ascending,
        Descending,

        Parameter
    }

    enum class WhereType(val display: String) {
        Equal("=="),
        NotEqual("!="),
        GreaterThan(">"),
        GreaterThanOrEqual(">="),
        LessThan("<"),
        LessThanOrEqual("<="),
        In("In"),
        ArrayContains("(*)"),
        BeforeTime("<T"),
        AfterTime(">T"),
        EqualNull("=?"),
        NotEqualNull("!=?")
    }

    enum class WhereTypeValue {
        String,
        Number,
        Timestamp,
        Boolean
    }

    enum class WhereInType {
        In,
        NotIn,
    }

    enum class WhereGroupType {
        And,
        Or,
    }

    @Serializable
    sealed class Where {
        @Serializable
        @SerialName("Value")
        data class Value(val field: String, val value: String, val compareType: WhereType, val typeValue: WhereTypeValue?): Where()
        @Serializable
        @SerialName("In")
        data class In(val field: String, val value: List<String>, val inType: WhereInType): Where()
        @Serializable
        @SerialName("Group")
        data class Group(val children: List<Where>, val groupType: WhereGroupType): Where()
    }

    enum class LoadStrategy(val display: String) {
        NetworkThenCache("Network then cache"),
        NetworkOnly("Network only"),
        CacheThenNetwork("Cache then network"),
        CacheOnly("Cache only"),
    }
}
